import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import type { BulkActionBarItem } from '../components/BulkActionBar';

export interface BulkActionsShortcutSession {
    selectedCount: number;
    actions: BulkActionBarItem[];
    onAction: (actionId: string) => void;
    isLoading?: boolean;
}

interface BulkActionsShortcutContextValue {
    session: BulkActionsShortcutSession | null;
    isMenuOpen: boolean;
    openMenu: () => void;
    closeMenu: () => void;
    toggleMenu: () => void;
    registerSession: (session: BulkActionsShortcutSession) => void;
    clearSession: () => void;
}

const BulkActionsShortcutContext = createContext<BulkActionsShortcutContextValue | null>(null);

export const BulkActionsShortcutProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const [session, setSession] = useState<BulkActionsShortcutSession | null>(null);
    const [isMenuOpen, setIsMenuOpen] = useState(false);

    const registerSession = useCallback((next: BulkActionsShortcutSession) => {
        setSession(next);
    }, []);

    const clearSession = useCallback(() => {
        setSession(null);
        setIsMenuOpen(false);
    }, []);

    const openMenu = useCallback(() => setIsMenuOpen(true), []);
    const closeMenu = useCallback(() => setIsMenuOpen(false), []);
    const toggleMenu = useCallback(() => setIsMenuOpen((open) => !open), []);

    useEffect(() => {
        if (!session || session.selectedCount <= 0) {
            setIsMenuOpen(false);
        }
    }, [session]);

    const value = useMemo(
        () => ({
            session,
            isMenuOpen,
            openMenu,
            closeMenu,
            toggleMenu,
            registerSession,
            clearSession,
        }),
        [session, isMenuOpen, openMenu, closeMenu, toggleMenu, registerSession, clearSession],
    );

    return (
        <BulkActionsShortcutContext.Provider value={value}>
            {children}
        </BulkActionsShortcutContext.Provider>
    );
};

export const useBulkActionsShortcut = (): BulkActionsShortcutContextValue => {
    const ctx = useContext(BulkActionsShortcutContext);
    if (!ctx) {
        throw new Error('useBulkActionsShortcut must be used within BulkActionsShortcutProvider');
    }
    return ctx;
};

/**
 * Registers the current page's bulk-action session while selection is active.
 * Pass null (or selectedCount <= 0) to clear. No-ops outside the provider.
 * Bars with shortcutEnabled=false pass null and must not clear another page's session.
 */
export const useRegisterBulkActionsShortcut = (
    session: BulkActionsShortcutSession | null,
): void => {
    const ctx = useContext(BulkActionsShortcutContext);
    const registerSession = ctx?.registerSession;
    const clearSession = ctx?.clearSession;
    const didRegisterRef = useRef(false);

    const selectedCount = session?.selectedCount ?? 0;
    const isLoading = session?.isLoading ?? false;
    const onAction = session?.onAction;
    const actions = session?.actions;
    const actionKey = actions?.map((a) => a.id).join('|') ?? '';

    useEffect(() => {
        if (!registerSession || !clearSession) return;

        if (!session || selectedCount <= 0 || !onAction || !actions) {
            if (didRegisterRef.current) {
                clearSession();
                didRegisterRef.current = false;
            }
            return;
        }

        registerSession({
            selectedCount,
            actions,
            onAction,
            isLoading,
        });
        didRegisterRef.current = true;

        return () => {
            clearSession();
            didRegisterRef.current = false;
        };
        // actionKey stands in for actions identity; onAction/actions read from this render.
        // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional stable registration deps
    }, [selectedCount, isLoading, actionKey, onAction, registerSession, clearSession]);
};

export default BulkActionsShortcutContext;
